import React from "react";

/**
 * Reusable next/prev pagination bar. Renders nothing when total <= pageSize.
 *
 * Props:
 *   total     — total number of rows (after filter)
 *   pageSize  — rows per page (default 50)
 *   page      — current 1-based page number
 *   onChange  — (nextPage: number) => void
 *   testId    — data-testid prefix (defaults to "pagination")
 */
export default function Pagination({
  total, pageSize = 50, page, onChange, testId = "pagination",
}) {
  if (!total || total <= pageSize) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const end = Math.min(start + pageSize, total);
  return (
    <div className="flex items-center justify-between mt-3 text-xs" data-testid={testId}>
      <span className="text-slate-500">
        Showing <b>{start + 1}</b>–<b>{end}</b> of <b>{total}</b>
      </span>
      <div className="inline-flex items-center gap-1">
        <button type="button" onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1}
          className="px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-40 font-bold uppercase"
          data-testid={`${testId}-prev`}>← Prev</button>
        <span className="px-2 text-slate-600" data-testid={`${testId}-indicator`}>
          Page {page} / {totalPages}
        </span>
        <button type="button" onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
          className="px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-40 font-bold uppercase"
          data-testid={`${testId}-next`}>Next →</button>
      </div>
    </div>
  );
}

/** useMemo helper: apply page slicing to any array. */
export function usePaged(rows, page, pageSize = 50) {
  return React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);
}
